import StyleSelect from '@chegg-wt/core/components/StyleSelect'
import { AppState } from '@chegg-wt/core/state'
import {
  COPY_SUCCESS_INTERSTITIAL_MODAL,
  COPY_SUCCESS_MODAL,
} from '@chegg-wt/core/state/modals/constants'
import { copyAll } from '@chegg-wt/core/utils/copy'
import React from 'react'
import { connect, useSelector } from 'react-redux'
import { OpenModalPayload } from '@chegg-wt/core/state/modals/types'
import {
  origins,
  UserClickPayload,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { AuthModalOrigin } from '@chegg-wt/core/components/Modals/AuthModal/types'
// import { VIEWPAGE_PLAGIARISM_EXPORT } from '@chegg-wt/core/state/optimizely/constants'
// import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import {
  CHANGE_STYLE_BTN,
  COPY_ALL_BTN,
  EXPORT_ALL_BTN,
} from '@chegg-wt/core/constants/biblography'
import { Citation } from '@chegg-wt/core/types/redux-citation-api'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { sortedCitationsSelector } from '@chegg-wt/core/state/bibliography/selectors'
import { shouldShowValidatorWarningSelector } from '@chegg-wt/core/state/citation/selectors'
import { currentStyleNameSelector } from '@chegg-wt/core/state/currentProject/selectors'
import { loggedInSelector, userRoleSelector } from '@chegg-wt/core/state/user/selectors'
import { styleIdToAlias, transformDeprecatedStyleId } from '@chegg-wt/core/utils/styles'
import { adConfigVersionSelector } from '@chegg-wt/core/state/featureFlags/selectors'
import { UserRole } from '@chegg-wt/core/constants/user'
import { Config } from '@chegg-wt/core/config'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import { AuthPayload } from '@chegg-wt/core/types/auth'
import CreateCitationButton from '../ViewPageTop/CreateCitationButton'
import * as Styled from './styled'

interface ExportButtonProps {
  openModal: (payload: OpenModalPayload) => void
  onExport: (citations: Citation[]) => void
  citations: Citation[]
  userClick: (payload: UserClickPayload) => void
  startAuthFlow: (payload: AuthPayload) => void
}

export interface Props extends ExportButtonProps {
  shouldShowWarning?: boolean
  currentStyleName: string
  adConfigVersion: string
  userRole: UserRole
  onStyleChangeSelect: (id: string, name: string) => void
  onStyleChangeClick: () => void
  userClick: (payload: UserClickPayload) => void
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  onExport,
  citations,
  userClick,
  startAuthFlow,
}) => {
  const isLoggedIn = useSelector(loggedInSelector)
  // const { variables } = useSafeOptimizelyFeature(VIEWPAGE_PLAGIARISM_EXPORT)
  // const { withExportGate } = variables
  const withExportGate = false
  const handleclick = () => {
    userClick({ origin: origins.exportAllCitationsClick })
    if (withExportGate && !isLoggedIn) {
      startAuthFlow({
        defaultFlow: 'signup',
        origin: AuthModalOrigin.VIEW_PAGE_EXPORT,
      })
    } else {
      onExport(citations)
    }
  }

  return (
    <Styled.ExportButton size="medium" data-test={EXPORT_ALL_BTN} onClick={handleclick}>
      <>
        <Styled.Export size="medium" />
        <span>Export all</span>
      </>
    </Styled.ExportButton>
  )
}

export const ButtonContainer: React.FC<Props> = ({
  citations,
  shouldShowWarning = false,
  openModal,
  currentStyleName,
  userRole,
  onStyleChangeClick,
  onStyleChangeSelect,
  onExport,
  userClick,
  startAuthFlow,
}) => {
  const hasCitations = citations.length > 0

  return (
    <Styled.CitationCTAContainer>
      <Styled.CitationButtonCTAContainer>
        <CreateCitationButton />
        <StyleSelect
          trigger={({ isOpen, openDropdown }) => (
            <Styled.CitationStyleButton
              onClick={() => {
                openDropdown()
                onStyleChangeClick()
              }}
              data-test={CHANGE_STYLE_BTN}
              aria-expanded={isOpen}
            >
              <Styled.ButtonText>{currentStyleName}</Styled.ButtonText>
              <Styled.Chevron pointing={isOpen ? 'up' : 'down'} />
            </Styled.CitationStyleButton>
          )}
          onSelectCallback={onStyleChangeSelect}
        />
      </Styled.CitationButtonCTAContainer>

      {/* NOTE (Re: WTAC-256) If this redesign test wins, QA tests may break due to missing Save button  */}

      {hasCitations && (
        <Styled.CitationUtilityCTAContainer>
          <Styled.CopyButton
            size="medium"
            data-test={COPY_ALL_BTN}
            onClick={() => {
              copyAll(citations)
              userClick({ origin: origins.copyAllCitationsClick })
              openModal({
                name:
                  userRole !== 'mybibpro' && Config.showInterstitialAd
                    ? COPY_SUCCESS_INTERSTITIAL_MODAL
                    : COPY_SUCCESS_MODAL,
                data: {
                  text: 'All citations',
                  shouldShowWarning,
                },
              })
            }}
          >
            <>
              <Styled.ImageWrapper>
                <Styled.CopyImage />
              </Styled.ImageWrapper>
              <span>Copy all </span>
            </>
          </Styled.CopyButton>
          <ExportButton
            openModal={openModal}
            onExport={onExport}
            citations={citations}
            userClick={userClick}
            startAuthFlow={startAuthFlow}
          />
        </Styled.CitationUtilityCTAContainer>
      )}
    </Styled.CitationCTAContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  citations: sortedCitationsSelector(state),
  shouldShowWarning: shouldShowValidatorWarningSelector(state),
  currentStyleName: currentStyleNameSelector(state),
  adConfigVersion: adConfigVersionSelector(state),
  userRole: userRoleSelector(state),
})

const mapDispatchToProps = {
  openModal,
  userClick,
  onStyleChangeClick: () => userClick({ origin: origins.styleChangeClick }),
  onStyleChangeSelect: (id: string, name: string) =>
    userClick({
      origin: origins.styleChangeSelect,
      value: name,
      style: styleIdToAlias(transformDeprecatedStyleId(id)),
    }),
  startAuthFlow,
}

export default connect(mapStateToProps, mapDispatchToProps)(ButtonContainer)
